'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';

const navItems = [
  {
    label: 'Products',
    href: '#products',
    dropdown: [
      { label: 'Ecom Envelopes', href: '#ecom-packaging' },
      { label: 'Ecom Boxes', href: '#ecom-packaging' },
      { label: 'Tapes & Tags', href: '#ecom-packaging' },
      { label: 'Stickers & Pouches', href: '#ecom-packaging' },
      { label: 'Glass Jars', href: '#ecom-packaging' },
      { label: 'Honeycomb Paper', href: '#ecom-packaging' },
      { label: 'Monocartons', href: '#ecom-packaging' },
      { label: 'Cotton Cloth Bags', href: '#apparel-packaging' },
      { label: 'Paper Carry Bags', href: '#apparel-packaging' },
    ],
  },
  { label: 'Customize', href: '#custom-packaging' },
  {
    label: 'Conference Kits',
    href: '#conference-kits',
    dropdown: [
      { label: 'Kraft Paper Folders', href: '#conference-kits' },
      { label: 'Cotton Lanyards', href: '#conference-kits' },
      { label: 'Seed Paper Notepads', href: '#conference-kits' },
      { label: 'Seed Pens', href: '#conference-kits' },
      { label: 'Kraft Certificates', href: '#conference-kits' },
    ],
  },
  { label: 'Samples', href: '#samples' },
  { label: 'About Us', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-white border-b border-gray-100 transition-shadow duration-200 ${
        isScrolled ? 'shadow-md' : ''
      }`}
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 flex-shrink-0">
            <Image
              src="/assets/images/Group_464-1786683986985.png"
              alt="Ecosange - Sustainable Packaging"
              width={140}
              height={48}
              className="h-10 lg:h-12 w-auto object-contain"
              priority
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems?.map((item) => (
              <div
                key={item?.label}
                className="relative"
                onMouseEnter={() => item?.dropdown && setActiveDropdown(item?.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <a
                  href={item?.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200 rounded-md hover:bg-primary/5"
                >
                  {item?.label}
                  {item?.dropdown && (
                    <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </a>
                {item?.dropdown && activeDropdown === item?.label && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-fade-in">
                    {item?.dropdown?.map((sub) => (
                      <a
                        key={sub?.label}
                        href={sub?.href}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-primary/5 hover:text-primary transition-colors duration-150 font-medium"
                      >
                        {sub?.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#custom-packaging"
              className="btn-primary text-sm px-5 py-2.5"
            >
              Get Custom Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-primary/10 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4 animate-fade-in">
          {navItems?.map((item) => (
            <div key={item?.label} className="mb-1">
              <a
                href={item?.href}
                className="block py-2.5 text-sm font-medium text-gray-700 hover:text-primary transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {item?.label}
              </a>
              {item?.dropdown && (
                <div className="pl-4 border-l-2 border-primary/20 ml-2 mb-2">
                  {item?.dropdown?.map((sub) => (
                    <a
                      key={sub?.label}
                      href={sub?.href}
                      className="block py-1.5 text-xs text-gray-500 hover:text-primary transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {sub?.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href="#custom-packaging"
            className="btn-primary w-full text-center mt-3 text-sm"
            onClick={() => setMobileOpen(false)}
          >
            Get Custom Quote
          </a>
        </div>
      )}
    </header>
  );
}
